'use client';

/**
 * (app)-only auth context: runs the silent guest bootstrap on mount, exposes
 * the guest-session stub and the post-session signup flow, and rehydrates an
 * authed session on reload. The uid (IdentityId) is the through-line on the
 * common, same-device path — read once, never overwritten, identical across
 * the guest -> account upgrade. A cross-device conflict is the exception: the
 * uid in effect becomes the login's OWN resolved identity, and reload always
 * restores from the persisted session (`session.identityId`), never from the
 * cached guest uid — see `linkAccount` in session.ts.
 *
 * State that must survive reload (uid, email, tokens, a pending cross-device
 * merge) goes through storage.ts. The signup password lives only in a ref for
 * the confirm step — never stored.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { track } from '@/lib/analytics/events';
import { identifyUser } from '@/lib/analytics/posthog';
import { authConfig } from './config';
import { SessionExpiredError } from './types';
import { authedFetch } from './api';
import { getCredentialsForIdentity, refreshIdToken } from './cognito';
import { beginGoogleSignIn as startGoogleSignIn, consumePendingGoogleFlow } from './hostedUi';
import {
  bootstrapGuest,
  confirmAndSignIn,
  linkAccount,
  realAuthDeps,
  type AuthDeps,
} from './session';
import type { AuthStatus, AuthTokens } from './types';
import type { SessionAuth } from '@/lib/training/api';

interface AuthContextValue {
  status: AuthStatus;
  /** The uid (Cognito IdentityId). */
  uid: string | null;
  email: string | null;
  tier: string | null;
  guestSessionId: string | null;
  /** Email awaiting a verification code (drives the code step). */
  pendingEmail: string | null;
  busy: boolean;
  error: string | null;
  retryBootstrap: () => void;
  /**
   * Resolve the credentials the training-session API needs for the current
   * tier: freshly-minted Identity-Pool guest creds (SigV4), or the cached JWT
   * + identity for an authed caller.
   */
  getSessionAuth: (options?: { refresh?: boolean }) => Promise<SessionAuth>;
  /** Mark the just-finished guest session done — flips the console to the signup prompt. */
  markSessionComplete: (sessionId: string) => void;
  beginSignUp: (email: string, password: string) => Promise<boolean>;
  completeSignUp: (code: string) => Promise<boolean>;
  /** Navigate to the Hosted UI to sign in with Google (PKCE authorization-code flow). */
  beginGoogleSignIn: () => void;
  /** True while an OAuth callback (code+state) is being exchanged and linked. */
  processingOAuthCallback: boolean;
  /**
   * Read (without clearing) the guest uid awaiting an account merge (set only
   * after a cross-device identity conflict was resolved during linking). Safe
   * to call repeatedly — a failed `session/complete` must be able to resend
   * the same uid on retry (the backend's MERGE_CLAIM is idempotent).
   */
  peekPendingMergeGuestUid: () => string | undefined;
  /** Clear the pending merge uid — call only after the `session/complete` that sent it succeeded. */
  clearPendingMergeGuestUid: () => void;
  /** Record whether the most recent `session/complete` response merged another device's guest state, for `AuthedPanel` copy. */
  reportMergeOutcome: (merged: boolean) => void;
  /** True if the most recent authed `session/complete` reported a merge; null before any close. */
  lastMergeOutcome: boolean | null;
  /** True from a resolved cross-device conflict until the merge-carrying `session/complete` succeeds — the uid changed but the outcome isn't known yet. */
  pendingMerge: boolean;
  dismissError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthState {
  status: AuthStatus;
  uid: string | null;
  email: string | null;
  tier: string | null;
  guestSessionId: string | null;
  pendingEmail: string | null;
  busy: boolean;
  error: string | null;
  processingOAuthCallback: boolean;
  lastMergeOutcome: boolean | null;
  pendingMerge: boolean;
}

const INITIAL: AuthState = {
  status: 'loading',
  uid: null,
  email: null,
  tier: null,
  guestSessionId: null,
  pendingEmail: null,
  busy: false,
  error: null,
  processingOAuthCallback: false,
  lastMergeOutcome: null,
  pendingMerge: false,
};

function friendlyError(err: unknown): string {
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    case 'UsernameExistsException':
      return 'An account with this email already exists.';
    case 'InvalidPasswordException':
      return 'Password needs 8+ characters with upper, lower, a number, and a symbol.';
    case 'InvalidParameterException':
      return 'Check the email and password and try again.';
    case 'CodeMismatchException':
      return "That code doesn't match. Check your email and try again.";
    case 'ExpiredCodeException':
      return 'That code expired. Request a new one.';
    case 'UserNotConfirmedException':
      return 'Confirm your email to finish signing in.';
    case 'NotAuthorizedException':
      return 'Incorrect email or password.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a moment and try again.';
    default:
      return err instanceof Error && err.message
        ? err.message
        : 'Something went wrong. Try again.';
  }
}

export function AuthProvider({
  children,
  deps,
}: {
  children: ReactNode;
  /** Injectable for tests; defaults to the real SDK + localStorage wiring. */
  deps?: AuthDeps;
}) {
  const authDeps = useMemo(() => deps ?? realAuthDeps(), [deps]);
  const [state, setState] = useState<AuthState>(INITIAL);
  const tokensRef = useRef<AuthTokens | null>(null);
  const signupRef = useRef<{ email: string; password: string } | null>(null);
  const pendingMergeGuestUidRef = useRef<string | null>(null);

  const patch = useCallback((next: Partial<AuthState>) => {
    setState((prev) => ({ ...prev, ...next }));
  }, []);

  const bootstrap = useCallback(async () => {
    patch({ status: 'loading', error: null });
    try {
      const guestUid = await bootstrapGuest(authDeps);
      const session = authDeps.storage.readSession();
      const live =
        session?.tokens &&
        (!session.tokens.expiresAt || session.tokens.expiresAt > Date.now());
      if (live && session) {
        // The device uid in effect is session.identityId, NOT the cached
        // guest uid — they diverge after a cross-device conflict resolves to
        // a different identity (linkAccount's previousGuestUid path). Using
        // the cached guest uid here would sign every authed API call with an
        // identity Cognito no longer accepts, breaking the session on every
        // reload for a cross-device sign-in.
        tokensRef.current = session.tokens;
        pendingMergeGuestUidRef.current = session.pendingMergeGuestUid ?? null;
        identifyUser(session.identityId);
        patch({
          status: 'authed',
          uid: session.identityId,
          email: session.email,
          error: null,
          pendingMerge: Boolean(session.pendingMergeGuestUid),
        });
      } else {
        if (session) authDeps.storage.clearSession();
        identifyUser(guestUid);
        patch({ status: 'guest', uid: guestUid, error: null });
      }
    } catch (err) {
      patch({ error: friendlyError(err) });
    }
  }, [authDeps, patch]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  /**
   * `refresh: true` asks for credentials minted now rather than whatever is cached.
   *
   * A caller passes it after a 401: a Cognito ID token lasts an hour, so a learner who trains past
   * that gets rejected on their next answer, and until this existed the app could only report the
   * status code (2026-08-26). For a guest nothing changes — their credentials are minted on every
   * call already — so this is the authed path catching up with the guest one.
   */
  const getSessionAuth = useCallback(async (options?: { refresh?: boolean }): Promise<SessionAuth> => {
    if (state.status === 'authed') {
      const tokens = tokensRef.current;
      if (!tokens || !state.uid) throw new Error('Not signed in.');
      if (options?.refresh && tokens.refreshToken) {
        const idToken = await refreshIdToken(tokens.refreshToken);
        // No token back means the refresh token is spent too, and only signing in again will do.
        if (!idToken) throw new SessionExpiredError();
        tokensRef.current = { ...tokens, idToken };
        return { kind: 'authed', idToken, identityId: state.uid };
      }
      return { kind: 'authed', idToken: tokens.idToken, identityId: state.uid };
    }
    if (!state.uid) throw new Error('Guest identity not ready yet.');
    const credentials = await getCredentialsForIdentity(state.uid);
    return { kind: 'guest', credentials };
  }, [state.status, state.uid]);

  const markSessionComplete = useCallback(
    (sessionId: string) => patch({ guestSessionId: sessionId }),
    [patch],
  );

  const peekPendingMergeGuestUid = useCallback(
    () => pendingMergeGuestUidRef.current ?? undefined,
    [],
  );

  const clearPendingMergeGuestUid = useCallback(() => {
    pendingMergeGuestUidRef.current = null;
    // Also drop it from the persisted session — otherwise a reload after a
    // successful merge-carrying close would rehydrate the stale pending uid
    // from storage and resend an already-merged guest uid on the next close.
    const session = authDeps.storage.readSession();
    if (session) {
      authDeps.storage.persistSession({ ...session, pendingMergeGuestUid: null });
    }
    patch({ pendingMerge: false });
  }, [authDeps, patch]);

  const reportMergeOutcome = useCallback(
    (merged: boolean) => patch({ lastMergeOutcome: merged }),
    [patch],
  );

  const beginSignUp = useCallback(
    async (email: string, password: string) => {
      patch({ busy: true, error: null });
      try {
        await authDeps.cognito.signUp(email, password);
        signupRef.current = { email, password };
        patch({ pendingEmail: email, busy: false });
        return true;
      } catch (err) {
        patch({ busy: false, error: friendlyError(err) });
        return false;
      }
    },
    [authDeps, patch],
  );

  /**
   * Link a set of User Pool tokens onto the cached guest uid, prove survival
   * at `/me`, persist the session, and flip to `authed`. Shared by the
   * email/code signup path and the Hosted UI Google callback — both reuse the
   * same `linkAccount` invariant (uid unchanged) instead of duplicating it.
   */
  const finishAuthedLink = useCallback(
    async (tokens: AuthTokens, fallbackEmail: string | null, method: 'email' | 'google') => {
      // Link the account onto the cached guest uid (uid unchanged on the
      // common path); a cross-device/returning-user conflict resolves to a
      // different identityId and reports the stale guest uid to merge.
      const { identityId, previousGuestUid } = await linkAccount(authDeps, tokens.idToken);
      pendingMergeGuestUidRef.current = previousGuestUid ?? null;
      const hasPendingMerge = previousGuestUid !== undefined;
      // Prove survival at the API: /me resolves the same uid + guest state.
      let tier: string | null = null;
      let email: string | null = fallbackEmail;
      try {
        const meRes = await authedFetch(
          { idToken: tokens.idToken, identityId, path: '/v1/me' },
          authConfig,
        );
        if (meRes.ok) {
          const me = (await meRes.json()) as { email?: string; tier?: string };
          tier = me.tier ?? null;
          email = me.email ?? fallbackEmail;
        }
      } catch {
        // /me is a nice-to-have proof; sign-in already succeeded.
      }
      tokensRef.current = tokens;
      authDeps.storage.persistSession({
        identityId,
        email,
        tokens,
        pendingMergeGuestUid: previousGuestUid ?? null,
      });
      patch({
        status: 'authed',
        uid: identityId,
        email,
        tier,
        pendingEmail: null,
        busy: false,
        pendingMerge: hasPendingMerge,
      });
      // The merge is sent on the first authed session/complete; if that close
      // fails the guest uid stays pending (peek, not consume) so the very
      // next close retries it — the backend's MERGE_CLAIM is idempotent.
      track('signup', { method });
    },
    [authDeps, patch],
  );

  const completeSignUp = useCallback(
    async (code: string) => {
      const creds = signupRef.current;
      if (!creds) {
        patch({ error: 'Start signup again — the session expired.' });
        return false;
      }
      patch({ busy: true, error: null });
      try {
        const tokens = await confirmAndSignIn(
          authDeps,
          creds.email,
          code,
          creds.password,
        );
        await finishAuthedLink(tokens, creds.email, 'email');
        signupRef.current = null;
        return true;
      } catch (err) {
        patch({ busy: false, error: friendlyError(err) });
        return false;
      }
    },
    [authDeps, patch, finishAuthedLink],
  );

  const beginGoogleSignIn = useCallback(() => {
    void startGoogleSignIn();
  }, []);

  // Handle the Hosted UI OAuth callback (code+state, or a declined-consent
  // error) once the guest bootstrap has resolved the cached uid. Guarded by a
  // ref so React 18 dev-mode's double-invoked effect never exchanges the same
  // single-use authorization code twice.
  const oauthHandledRef = useRef(false);
  useEffect(() => {
    if (state.status === 'loading') return;
    if (oauthHandledRef.current) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    const code = params.get('code');
    const returnedState = params.get('state');
    if (!oauthError && !(code && returnedState)) return;

    oauthHandledRef.current = true;
    const clearQuery = () =>
      window.history.replaceState(null, '', window.location.pathname);

    if (oauthError) {
      clearQuery();
      patch({
        error:
          oauthError === 'access_denied'
            ? 'Google sign-in was cancelled.'
            : 'Google sign-in failed. Try again.',
      });
      return;
    }

    void (async () => {
      patch({ processingOAuthCallback: true, error: null });
      try {
        const pending = consumePendingGoogleFlow();
        if (!pending || pending.state !== returnedState) {
          patch({ error: 'That sign-in link expired. Try again.' });
          return;
        }
        const tokens = await authDeps.cognito.exchangeGoogleCode(
          code as string,
          pending.codeVerifier,
          window.location.origin,
        );
        await finishAuthedLink(tokens, null, 'google');
      } catch (err) {
        patch({ error: friendlyError(err) });
      } finally {
        clearQuery();
        patch({ processingOAuthCallback: false });
      }
    })();
  }, [state.status, authDeps, patch, finishAuthedLink]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: state.status,
      uid: state.uid,
      email: state.email,
      tier: state.tier,
      guestSessionId: state.guestSessionId,
      pendingEmail: state.pendingEmail,
      busy: state.busy,
      error: state.error,
      processingOAuthCallback: state.processingOAuthCallback,
      lastMergeOutcome: state.lastMergeOutcome,
      pendingMerge: state.pendingMerge,
      retryBootstrap: () => void bootstrap(),
      getSessionAuth,
      markSessionComplete,
      beginSignUp,
      completeSignUp,
      beginGoogleSignIn,
      peekPendingMergeGuestUid,
      clearPendingMergeGuestUid,
      reportMergeOutcome,
      dismissError: () => patch({ error: null }),
    }),
    [
      state,
      bootstrap,
      getSessionAuth,
      markSessionComplete,
      beginSignUp,
      completeSignUp,
      beginGoogleSignIn,
      peekPendingMergeGuestUid,
      clearPendingMergeGuestUid,
      reportMergeOutcome,
      patch,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
